import { getItem, type WorldEventEnded, WORLD_EVENT_NAMES } from '@shefira/shared';

import { closeButton } from './hud.js';
import { element } from './kit.js';
import { uiFormat, uiText } from './text.js';
import { type PortraitCache, standingRow } from './world-event-standings.js';
import { absentLine, goldLine, resultTitle } from './world-event-text.js';
import './world-events.css';

/**
 * The end of a world event (ADR 0050): won or lost, what the player earned
 * and the whole ranking, heads included. Opened for whoever took part;
 * closed by its cross or Escape.
 */
export class WorldEventResult {
  private readonly root = element('section', 'event-result');
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || this.root.hidden) return;
    event.stopPropagation();
    this.close();
  };

  constructor(private readonly portraits: PortraitCache) {
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog');
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKeyDown, true);
  }

  show(result: WorldEventEnded): void {
    this.root.className = `event-result event-result--${result.outcome}`;
    const title = element(
      'h2',
      'event-result__title',
      resultTitle(result, uiText(WORLD_EVENT_NAMES[result.kind])),
    );
    this.root.setAttribute('aria-label', title.textContent);
    const verdict = element('p', 'event-result__verdict', this.verdict(result));
    const children: HTMLElement[] = [
      closeButton(uiText('Fermer'), () => {
        this.close();
      }),
      title,
      verdict,
    ];
    if (result.reward !== null) {
      const items = result.reward.items
        .map((item) => {
          const name = getItem(item.templateId)?.name ?? item.templateId;
          return item.quantity > 1 ? `${name} x${String(item.quantity)}` : name;
        })
        .join(', ');
      children.push(
        element(
          'p',
          'event-result__reward',
          items === ''
            ? uiFormat('+{xp} XP (à tes pieds)', { xp: result.reward.xp })
            : uiFormat('+{xp} XP · {items} (à tes pieds)', { xp: result.reward.xp, items }),
        ),
      );
      const gold = goldLine(result);
      if (gold !== '') children.push(element('p', 'event-result__reward', gold));
    }
    const list = element('ol', 'event-result__list');
    list.append(
      ...result.ranking.map((line) =>
        standingRow(line, this.portraits, result.self !== null && line.rank === result.self.rank),
      ),
    );
    children.push(list);
    this.root.replaceChildren(...children);
    this.root.hidden = false;
  }

  close(): void {
    this.root.hidden = true;
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown, true);
    this.root.remove();
  }

  private verdict(result: WorldEventEnded): string {
    const self = result.self;
    if (result.outcome !== 'won') return 'Personne n’est récompensé.';
    if (self === null || self.rank === 0) return absentLine(result);
    const place = uiFormat('{rank}e sur {total}, {percent} % des points', {
      rank: self.rank,
      total: result.ranking.length,
      percent: self.percent,
    });
    return self.eligible
      ? uiFormat('{place} : récompense gagnée.', { place })
      : uiFormat('{place} : pas assez pour la récompense ({threshold} points).', {
          place,
          threshold: self.threshold,
        });
  }
}
